import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";

import type { Database } from "@/integrations/supabase/types";

export type CaseStudy = Database["public"]["Tables"]["case_studies"]["Row"];
export type Testimonial = Database["public"]["Tables"]["testimonials"]["Row"];
export type TeamMember = Database["public"]["Tables"]["team_members"]["Row"];
export type SiteStat = Database["public"]["Tables"]["site_stats"]["Row"];

/**
 * Public read-only content loaders.
 *
 * These run server-side with the publishable key, so RLS applies as `anon`:
 * only published rows (and, for testimonials/stats, only permissioned or
 * substantiated ones) can ever come back. Every loader degrades to an empty
 * result on error — a database hiccup hides a section, it never crashes a page.
 */
function publicClient() {
  return createClient<Database>(
    process.env["SUPABASE_URL"]!,
    process.env["SUPABASE_PUBLISHABLE_KEY"]!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

/**
 * Short-lived module-scope cache. These tables change rarely (and are empty
 * today), so without this every single server render pays a database
 * round-trip before it can emit a byte of HTML. 60s is short enough that new
 * content appears promptly and long enough to take the query off the hot path.
 */
const TTL_MS = 60_000;
const cache = new Map<string, { at: number; value: unknown }>();

async function cached<T>(key: string, load: () => Promise<T>): Promise<T> {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.value as T;
  const value = await load();
  cache.set(key, { at: Date.now(), value });
  return value;
}

export const getCaseStudies = createServerFn({ method: "GET" })
  .inputValidator((data: { limit?: number } | undefined) => data ?? {})
  .handler(async ({ data }): Promise<CaseStudy[]> =>
    cached(`case_studies:${data.limit ?? "all"}`, async () => {
      try {
        let query = publicClient()
          .from("case_studies")
          .select("*")
          .eq("published", true)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true });
        if (data.limit) query = query.limit(data.limit);
        const { data: rows, error } = await query;
        if (error) {
          console.error("getCaseStudies failed", error);
          return [];
        }
        return rows ?? [];
      } catch (error) {
        console.error("getCaseStudies threw", error);
        return [];
      }
    }),
  );

export const getCaseStudyBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }): Promise<CaseStudy | null> =>
    cached(`case_study:${data.slug}`, async () => {
      try {
        const { data: row, error } = await publicClient()
          .from("case_studies")
          .select("*")
          .eq("published", true)
          .eq("slug", data.slug)
          .maybeSingle();
        if (error) {
          console.error("getCaseStudyBySlug failed", error);
          return null;
        }
        return row ?? null;
      } catch (error) {
        console.error("getCaseStudyBySlug threw", error);
        return null;
      }
    }),
  );

export const getTestimonials = createServerFn({ method: "GET" })
  .inputValidator((data: { limit?: number } | undefined) => data ?? {})
  .handler(async ({ data }): Promise<Testimonial[]> =>
    cached(`testimonials:${data.limit ?? "all"}`, async () => {
      try {
        let query = publicClient()
          .from("testimonials")
          .select("*")
          .eq("published", true)
          .eq("permission_on_file", true)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true });
        if (data.limit) query = query.limit(data.limit);
        const { data: rows, error } = await query;
        if (error) {
          console.error("getTestimonials failed", error);
          return [];
        }
        return rows ?? [];
      } catch (error) {
        console.error("getTestimonials threw", error);
        return [];
      }
    }),
  );

export const getTeamMembers = createServerFn({ method: "GET" }).handler(
  async (): Promise<TeamMember[]> =>
    cached("team_members", async () => {
      try {
        const { data: rows, error } = await publicClient()
          .from("team_members")
          .select("*")
          .eq("published", true)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true });
        if (error) {
          console.error("getTeamMembers failed", error);
          return [];
        }
        return rows ?? [];
      } catch (error) {
        console.error("getTeamMembers threw", error);
        return [];
      }
    }),
);

export const getSiteStats = createServerFn({ method: "GET" }).handler(
  async (): Promise<SiteStat[]> =>
    cached("site_stats", async () => {
      try {
        const { data: rows, error } = await publicClient()
          .from("site_stats")
          .select("*")
          .eq("published", true)
          .eq("substantiated", true)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true });
        if (error) {
          console.error("getSiteStats failed", error);
          return [];
        }
        return rows ?? [];
      } catch (error) {
        console.error("getSiteStats threw", error);
        return [];
      }
    }),
);
